import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { Store, Plus, Trash2, Link2, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PLATFORMS = {
  shopify: { label: "Shopify", color: "#95BF47", mode: "Autopilot" },
  printify: { label: "Printify", color: "#39B54A", mode: "Autopilot" },
  printful: { label: "Printful", color: "#3b82f6", mode: "Autopilot" },
  etsy: { label: "Etsy", color: "#F1641E", mode: "Assisted only" },
};

export default function Stores() {
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("shopify");
  const [apiKey, setApiKey] = useState("");

  const load = () => api.get("/stores").then(({ data }) => setStores(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) { toast.error("Name the store."); return; }
    try {
      await api.post("/stores", { name, platform, api_key: apiKey });
      toast.success(apiKey ? "Store connected." : "Store added (not yet connected — add an API key to enable sync).");
      setOpen(false); setName(""); setApiKey("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not add store"));
    }
  };

  const remove = async (id) => {
    await api.delete(`/stores/${id}`);
    toast.success("Store removed.");
    load();
  };

  return (
    <>
      <PageHeader title="Connected Stores" subtitle="Connect Shopify, Printify/Printful and Etsy. Publish & fulfillment calls are mocked until you add live API keys." testid="stores-header">
        <button data-testid="add-store-button" onClick={() => setOpen(true)} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors flex items-center gap-2"><Plus className="w-4 h-4" /> Connect Store</button>
      </PageHeader>

      <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2.5">
        <ShieldCheck className="w-4 h-4" /> MOCKED: real Shopify/Printify/Etsy API calls are simulated in v1. Add your keys to go live.
      </div>

      {stores.length === 0 ? (
        <Card className="p-6"><EmptyState icon={Store} title="No stores connected" hint="Connect your first store to start publishing products." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {stores.map((s) => {
            const p = PLATFORMS[s.platform] || { label: s.platform, color: "#64748b", mode: "" };
            return (
              <Card key={s.id} className="p-5" data-testid={`store-card-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center font-head font-bold text-slate-950" style={{ backgroundColor: p.color }}>{p.label[0]}</div>
                    <div>
                      <h3 className="font-head text-base font-semibold text-slate-100">{s.name}</h3>
                      <span className="text-xs text-slate-500">{p.label} · {p.mode}</span>
                    </div>
                  </div>
                  <button data-testid={`delete-store-${s.id}`} onClick={() => remove(s.id)} className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <Pill tone={s.connected ? "emerald" : "amber"}>{s.connected ? "Connected" : "Not connected"}</Pill>
                  {s.api_key_masked && <span className="text-xs text-slate-500 font-mono flex items-center gap-1"><Link2 className="w-3 h-3" />{s.api_key_masked}</span>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#12161F] border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">Connect a store</DialogTitle>
            <DialogDescription className="text-slate-400">Add your store details. API calls are mocked until you provide a live key.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Store name</label>
              <input data-testid="store-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" placeholder="e.g. Nomad Threads" />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Platform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="store-platform-select" className="mt-1 bg-[#0C0F14] border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12161F] border-slate-800 text-slate-100">
                  {Object.entries(PLATFORMS).map(([k, v]) => <SelectItem key={k} value={k} className="focus:bg-slate-800">{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono uppercase tracking-wider">API key (optional in v1)</label>
              <input data-testid="store-apikey-input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg bg-[#0C0F14] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm font-mono" placeholder="shpat_… (stored securely)" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="confirm-add-store-button" onClick={add} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400">Connect</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
